import {
  ActionButton,
  DashboardType,
  DataObjectVersionType,
  DataVersionStatus,
  formatDate,
  FormLabel,
  GroupDescriptionOption,
  ISODate,
  Detail,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  VersionDisplay,
} from '@atlas-frontend/sdk';
import { WithDescriptionId } from './CommentsModal';
import { Flex, Select } from 'antd';
import translations from './DetailsModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SourcesTable } from './SourcesTable';
import { AlgorithmsDetails } from './AlgorithmsDetails';

const { addTranslations, t } = i18nNsFactory('visual-validation-details-modal');
addTranslations(translations);

type CrossTableDetailsType = {
  type: DashboardType.CROSSTABLE | DashboardType.REPORT;
  version: DataObjectVersionType;
};

type TimeSeriesDetailsType = {
  type: DashboardType.TIME_SERIES;
  versions: DataObjectVersionType[];
};

type ComparisonDetailsType = {
  type: DashboardType.COMPARISON;
  version: DataObjectVersionType;
  versionOther: DataObjectVersionType;
};

type TimeSeriesComparisonDetailsType = {
  type: DashboardType.TIME_SERIES_COMPARISON;
  versions: DataObjectVersionType[];
  versionsOther: DataObjectVersionType[];
};

export type ModalData = {
  descriptionOption: GroupDescriptionOption & WithDescriptionId;
} & (
  | CrossTableDetailsType
  | TimeSeriesDetailsType
  | ComparisonDetailsType
  | TimeSeriesComparisonDetailsType
);

type ModalProps = ModalData & { onClose: () => void };

type DetailsModalBaseProps = ModalProps & {
  title: string;
  children: (selectedVersion: DataObjectVersionType) => ReactNode;
};

const DetailsModalBase: React.FC<DetailsModalBaseProps> = (props) => {
  const { title, onClose, children } = props;
  const [selectedVersion, setSelectedVersion] = useState<DataObjectVersionType>(
    getInitialVersion(props),
  );

  return (
    <Modal
      onCancel={onClose}
      width="80rem"
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="mainAction" onClick={onClose}>
              {t('close')}
            </ActionButton>
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>{title}</TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <Padding scrollable>
        <Description
          {...props}
          selectedVersionId={selectedVersion.id}
          onVersionChange={setSelectedVersion}
        />
        <Padding top>{children(selectedVersion)}</Padding>
      </Padding>
    </Modal>
  );
};

export const SourcesModal: React.FC<ModalProps> = (props) => (
  <DetailsModalBase title={t('title.sources.' + props.type)} {...props}>
    {(selectedVersion) => <SourcesTable versionId={selectedVersion.id} />}
  </DetailsModalBase>
);

export const AlgorithmsModal: React.FC<
  ModalProps & {
    initialAlgorithmCode?: string;
    initialAlgorithmTargetSequenceNumber?: number | null;
  }
> = (props) => (
  <DetailsModalBase title={t('title.algorithms.' + props.type)} {...props}>
    {(selectedVersion) => (
      <AlgorithmsDetails
        versionId={selectedVersion.id}
        outputType={props.descriptionOption.outputType}
        initialAlgorithmCode={props.initialAlgorithmCode}
        initialAlgorithmTargetSequenceNumber={
          props.initialAlgorithmTargetSequenceNumber
        }
      />
    )}
  </DetailsModalBase>
);

type DescriptionProps = {
  descriptionOption: GroupDescriptionOption & WithDescriptionId;
  selectedVersionId: number;
  onVersionChange: (version: DataObjectVersionType) => void;
} & (
  | CrossTableDetailsType
  | TimeSeriesDetailsType
  | ComparisonDetailsType
  | TimeSeriesComparisonDetailsType
);

const Description = (props: DescriptionProps) => {
  switch (props.type) {
    case DashboardType.CROSSTABLE:
    case DashboardType.REPORT:
      return <CrossTableDescription {...props} />;
    case DashboardType.TIME_SERIES:
      return <TimeSeriesDescription {...props} />;
    case DashboardType.COMPARISON:
      return <ComparisonDescription {...props} />;
    case DashboardType.TIME_SERIES_COMPARISON:
      return <TimeSeriesComparisonDescription {...props} />;
    default:
      return null;
  }
};

const CrossTableDescription: React.FC<
  {
    descriptionOption: GroupDescriptionOption & WithDescriptionId;
  } & CrossTableDetailsType
> = ({ descriptionOption, version }) => {
  return (
    <Flex vertical gap="small">
      <Detail label={t('description.output')}>{descriptionOption.label}</Detail>
      <Detail label={t('description.crosstable.valueDate')}>
        {formatDate(version.valueDate)}
      </Detail>
      <Detail label={t('description.crosstable.version')}>
        {VersionLabel(version)}
      </Detail>
    </Flex>
  );
};

const TimeSeriesDescription: React.FC<
  {
    descriptionOption: GroupDescriptionOption & WithDescriptionId;
  } & TimeSeriesDetailsType & {
      selectedVersionId?: number;
      onVersionChange: (version: DataObjectVersionType) => void;
    }
> = ({ descriptionOption, versions, selectedVersionId, onVersionChange }) => {
  const options = versions
    .filter((ver) => !!ver.valueDate)
    .toSorted((v1, v2) => v1.valueDate!.localeCompare(v2.valueDate!))
    .map((ver) => ({
      label: formatDate(ver.valueDate),
      value: ver.id,
    }));
  const selectedVersion = versions.find((ver) => ver.id === selectedVersionId)!;
  const selectId = 'visual-validation-details-modal-version-select';

  return (
    <Flex vertical gap="small">
      <Detail label={t('description.output')}>{descriptionOption.label}</Detail>
      <Padding top>
        <Flex gap="middle" align="top">
          <Flex vertical gap="small">
            <FormLabel htmlFor={selectId} required>
              {t('description.timeseries.valueDate')}
            </FormLabel>
            <Select
              id={selectId}
              value={selectedVersionId}
              options={options}
              onChange={(id) =>
                onVersionChange(versions.find((v) => v.id === id)!)
              }
              style={{ minWidth: '18ch' }}
            />
          </Flex>
          <Flex vertical gap="small">
            <FormLabel required>
              {t('description.timeseries.version')}
            </FormLabel>
            <VersionLabel {...selectedVersion} />
          </Flex>
        </Flex>
      </Padding>
    </Flex>
  );
};

const ComparisonDescription: React.FC<
  {
    descriptionOption: GroupDescriptionOption & WithDescriptionId;
  } & ComparisonDetailsType & {
      selectedVersionId?: number;
      onVersionChange: (version: DataObjectVersionType) => void;
    }
> = ({
  descriptionOption,
  selectedVersionId,
  version,
  versionOther,
  onVersionChange,
}) => {
  const options = [
    {
      label: getComparisonOptionLabel(
        version,
        t('description.comparison.comparedData1'),
      ),
      value: version.id,
    },
    {
      label: getComparisonOptionLabel(
        versionOther,
        t('description.comparison.comparedData2'),
      ),
      value: versionOther.id,
    },
  ];
  const selectId = 'visual-validation-details-modal-version-select';

  return (
    <Flex vertical gap="small">
      <Detail label={t('description.output')}>{descriptionOption.label}</Detail>
      <Padding top>
        <Flex gap="middle" align="top">
          <Flex vertical gap="small">
            <FormLabel htmlFor={selectId} required>
              {t('description.comparison.usedData')}
            </FormLabel>
            <Select
              id={selectId}
              value={selectedVersionId}
              options={options}
              onChange={(id) =>
                onVersionChange(
                  [version, versionOther].find((v) => v.id === id)!,
                )
              }
              style={{ minWidth: '18ch' }}
            />
          </Flex>
        </Flex>
      </Padding>
    </Flex>
  );
};

type VersionsByValueDate = Record<
  ISODate,
  { primary: DataObjectVersionType | null; other: DataObjectVersionType | null }
>;

const TimeSeriesComparisonDescription: React.FC<
  {
    descriptionOption: GroupDescriptionOption & WithDescriptionId;
  } & TimeSeriesComparisonDetailsType & {
      selectedVersionId?: number;
      onVersionChange: (version: DataObjectVersionType) => void;
    }
> = ({
  descriptionOption,
  versions,
  versionsOther,
  selectedVersionId,
  onVersionChange,
}) => {
  const valueDateSelectId = 'visual-validation-details-modal-value-date-select';
  const versionSelectId = 'visual-validation-details-modal-version-select';

  const valueDates = useMemo(
    () =>
      [...versions, ...versionsOther]
        .map((ver) => ver.valueDate)
        .filter((date): date is ISODate => !!date)
        .filter((date, index, self) => self.indexOf(date) === index)
        .toSorted(),
    [versions, versionsOther],
  );
  const [valueDate, setValueDate] = useState<ISODate>(valueDates[0]);
  const [versionKey, setVersionKey] = useState<string | undefined>();

  const handleValueDateChange = (newValueDate: ISODate) => {
    setValueDate(newValueDate);

    const pair = versionsByValueDate[newValueDate];
    const nextKey = pair?.primary
      ? `${pair.primary.id}:0`
      : pair?.other
        ? `${pair.other.id}:1`
        : undefined;

    setVersionKey(nextKey);
    const nextVersion = nextKey
      ? [...versions, ...versionsOther].find(
          (v) => v.id === versionKeyToId(nextKey),
        )
      : undefined;
    if (nextVersion) onVersionChange(nextVersion);
  };
  // Up to two versions can exist per value date.
  const versionsByValueDate = useMemo<VersionsByValueDate>(() => {
    const record: VersionsByValueDate = {};
    for (const version of versions) {
      if (!version.valueDate) continue;
      if (!record[version.valueDate]) {
        record[version.valueDate] = { primary: version, other: null };
      } else {
        record[version.valueDate].primary = version;
      }
    }

    for (const version of versionsOther) {
      if (!version.valueDate) continue;
      if (!record[version.valueDate]) {
        record[version.valueDate] = { primary: null, other: version };
      } else {
        record[version.valueDate].other = version;
      }
    }

    return record;
  }, [versions, versionsOther]);

  const versionOptions = useMemo(() => {
    const opts: {
      label: ReactNode;
      value: string;
    }[] = [];
    const versionsForDate = versionsByValueDate[valueDate];

    if (!versionsForDate) {
      return opts;
    }
    const { primary, other } = versionsForDate;

    if (primary) {
      opts.push({
        label: getTimeSeriesComparisonOptionLabel(
          primary,
          t('description.timeseriesComparison.comparedData1'),
        ),
        value: primary.id + ':0',
      });
    }

    if (other) {
      opts.push({
        label: getTimeSeriesComparisonOptionLabel(
          other,
          t('description.timeseriesComparison.comparedData2'),
        ),
        value: other.id + ':1',
      });
    }

    return opts;
  }, [valueDate, versionsByValueDate]);

  useEffect(() => {
    if (!versionOptions.length) {
      setVersionKey(undefined);
      return;
    }

    setVersionKey((curr) => {
      // If current selection is still valid for this date, keep it
      if (curr && versionOptions.some((o) => o.value === curr)) return curr;

      // Try to select based on selectedVersionId
      if (selectedVersionId != null) {
        const matches = versionOptions.filter(
          (o) => versionKeyToId(o.value) === selectedVersionId,
        );
        if (matches.length === 1) return matches[0].value;
        if (matches.length > 1) return matches[0].value;
      }

      // Otherwise pick the first option.
      return versionOptions[0].value;
    });
  }, [versionOptions, selectedVersionId]);

  const handleVersionChange = useCallback(
    (selectedValue: string) => {
      setVersionKey(selectedValue);
      const version = [...versions, ...versionsOther].find(
        (v) => v.id === versionKeyToId(selectedValue),
      );
      if (version) onVersionChange(version);
    },
    [onVersionChange, versions, versionsOther],
  );

  return (
    <Flex vertical gap="small">
      <Detail label={t('description.output')}>{descriptionOption.label}</Detail>
      <Padding top>
        <Flex gap="middle" align="top">
          <Flex vertical gap="small">
            <FormLabel required htmlFor={valueDateSelectId}>
              {t('description.timeseriesComparison.valueDate')}
            </FormLabel>
            <Select
              id={valueDateSelectId}
              value={valueDate}
              options={valueDates.map((date) => ({
                label: formatDate(date),
                value: date,
              }))}
              onChange={handleValueDateChange}
              style={{ minWidth: '18ch' }}
            />
          </Flex>
          <Flex vertical gap="small">
            <FormLabel required htmlFor={versionSelectId}>
              {t('description.timeseriesComparison.version')}
            </FormLabel>
            <Select
              id={versionSelectId}
              options={versionOptions}
              value={versionKey}
              onChange={handleVersionChange}
              style={{ minWidth: '18ch' }}
            />
          </Flex>
        </Flex>
      </Padding>
    </Flex>
  );
};

const versionKeyToId = (versionKey: string): number => {
  const [versionIdStr] = versionKey.split(':');
  return parseInt(versionIdStr, 10);
};

const getComparisonOptionLabel = (
  version: DataObjectVersionType,
  suffix: string,
): ReactNode => (
  <Flex gap="small" align="center">
    <span>{formatDate(version.valueDate)}</span>
    <VersionLabel {...version} />
    <span>({suffix})</span>
  </Flex>
);

const getTimeSeriesComparisonOptionLabel = (
  version: DataObjectVersionType,
  suffix: string,
): ReactNode => (
  <Flex gap="small" align="center">
    <VersionLabel {...version} />
    <span>({suffix})</span>
  </Flex>
);

const VersionLabel = (version: DataObjectVersionType) => {
  const { statusCode, versionNumber, minorNumber } = version;
  if (!statusCode) return null;
  return (
    <VersionDisplay
      status={statusCode}
      versionNumber={versionNumber}
      minorNumber={minorNumber}
    >
      <DataVersionStatus status={statusCode} />
    </VersionDisplay>
  );
};

const getInitialVersion = (props: ModalData): DataObjectVersionType => {
  switch (props.type) {
    case DashboardType.CROSSTABLE:
    case DashboardType.REPORT:
    case DashboardType.COMPARISON:
      return props.version;
    case DashboardType.TIME_SERIES:
    case DashboardType.TIME_SERIES_COMPARISON:
      return props.versions
        .filter((ver) => !!ver.valueDate)
        .toSorted((v1, v2) => v1.valueDate!.localeCompare(v2.valueDate!))[0];
  }
};
